// /app/frontend/src/components/frugal-v2/tabs/HomeTab.jsx
// Main home tab - sections ordered by cascade flow with funding status

import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import ZenCard from '../components/ZenCard';
import FrugalCurtain from '../components/FrugalCurtain';
import IncomesSection from '../sections/IncomesSection';
import ExpensesSection from '../sections/ExpensesSection';
import ContingencySection from '../sections/ContingencySection';
import ContingencyReserveSection from '../sections/ContingencyReserveSection';
import SavingsSection from '../sections/SavingsSection';
import TransactionsSection from '../sections/TransactionsSection';
import ScanReceiptCard from '../components/ScanReceiptCard';

/**
 * Calculate cascade funding status for each bucket.
 * Money flows: Income -> Expenses -> Vital -> Contingency -> Savings -> Transactions
 * Returns: 'funded' (100%), 'partial' (some), 'none' ($0)
 */
function calcCascade(incomes, expenses, zen, savings, contingency) {
  // Use backend-computed values as the single source of truth.
  // Backend already handles: vital-set detection, recurring-vital exclusion,
  // contingency overflow, etc. Recomputing here causes drift.
  const totals = zen?.totals || {};
  const totalIncome = totals.income ?? (incomes || []).reduce((s, i) => s + (i.amount || 0), 0);
  const vitalBudget = totals.vital_budget || 0;
  const vitalFunded = totals.vital_funded ?? vitalBudget;
  const recurringTotal = totals.recurring_expenses || 0;
  const recurringPaid = (expenses || [])
    .filter(e => e.recurring !== false && e.is_paid)
    .reduce((s, e) => s + (e.amount || 0), 0);
  const contingencyBudget = zen?.contingency_budget || 0;
  const contingencyRemaining = zen?.contingency_balance ?? 0;
  const totalSavings = totals.savings ?? (savings || []).reduce((s, sv) => s + (sv.amount || 0), 0);
  const savingsDone = totals.savings_done || 0;

  const status = (have, need) =>
    need <= 0 ? 'funded'
    : have >= need ? 'funded'
    : have > 0 ? 'partial'
    : 'none';

  const result = {
    expenses: status(recurringPaid, recurringTotal),
    vital: status(vitalFunded, vitalBudget),
    contingency: status(contingencyRemaining, contingencyBudget),
    savings: status(savingsDone, totalSavings),
  };
  // remaining for "transactions" (zen daily) bucket — uses backend availability
  const available = totals.available ?? 0;
  result.transactions = available > 0 ? 'funded' : available === 0 ? 'none' : 'partial';
  // Total income echoed for callers that may need it
  result._income = totalIncome;
  return result;
}

const HomeTab = ({
  incomes, savings, contingency, expenses, transactions,
  vital_transactions, zen, language, token, subscriptionStatus,
  addIncome, editIncome, toggleIncome, removeIncome,
  addSaving, editSaving, toggleSaving, removeSaving,
  addExpense, toggleExpense, editExpense, removeExpense,
  addTransaction, editTransaction, removeTransaction,
  onRefresh, t
}) => {
  const [isCurtainOpen, setIsCurtainOpen] = useState(false);
  const isTrial = subscriptionStatus === 'trial' || subscriptionStatus === 'trialing';
  const inactivityTimer = useRef(null);

  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    if (isCurtainOpen) {
      inactivityTimer.current = setTimeout(() => setIsCurtainOpen(false), 30000);
    }
  }, [isCurtainOpen]);

  useEffect(() => {
    if (!isCurtainOpen) {
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
      return;
    }
    resetInactivityTimer();
    const events = ['touchstart', 'scroll', 'click'];
    events.forEach(e => window.addEventListener(e, resetInactivityTimer, { passive: true }));
    return () => {
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
      events.forEach(e => window.removeEventListener(e, resetInactivityTimer));
    };
  }, [isCurtainOpen, resetInactivityTimer]);

  const cascade = useMemo(() =>
    calcCascade(incomes, expenses, zen, savings, contingency),
    [incomes, expenses, zen, savings, contingency]
  );

  return (
    <div className="space-y-4 pb-20">
      <ZenCard 
        zen={zen} token={token} t={t} language={language}
        incomes={incomes} expenses={expenses}
        savings={savings} transactions={transactions}
      />

      <FrugalCurtain 
        isOpen={isCurtainOpen}
        onToggle={() => setIsCurtainOpen(!isCurtainOpen)}
        isTrial={isTrial} token={token} t={t} language={language}
      />

      {isCurtainOpen && (
        <div className="space-y-4 animate-slideUp">
          <IncomesSection
            incomes={incomes} onAdd={addIncome}
            onEdit={editIncome} onToggle={toggleIncome}
            onDelete={removeIncome} token={token} t={t}
          />

          <SavingsSection
            savings={savings} onAdd={addSaving}
            onEdit={editSaving} onToggle={toggleSaving}
            onDelete={removeSaving} t={t}
            fundingStatus={cascade.savings}
          />

          <ExpensesSection
            expenses={expenses} onAdd={addExpense}
            onToggle={toggleExpense} onEdit={editExpense}
            onDelete={removeExpense} token={token} t={t}
            fundingStatus={cascade.expenses}
          />

          <ContingencySection
            contingency={contingency} zen={zen}
            vitalTransactions={vital_transactions || []} t={t}
            fundingStatus={cascade.vital}
            onEditVital={editTransaction}
            onDeleteVital={removeTransaction}
          />

          <ContingencyReserveSection
            zen={zen} token={token} t={t}
            onUpdate={onRefresh}
            fundingStatus={cascade.contingency}
          />

          <TransactionsSection
            transactions={transactions} onAdd={addTransaction}
            onEdit={editTransaction} onDelete={removeTransaction}
            token={token} t={t}
            fundingStatus={cascade.transactions}
            availableAmount={zen?.totals?.available || 0}
          />

          {/* Last card: Scan Receipt - standalone, outside any container */}
          <ScanReceiptCard token={token} t={t} onAdd={addTransaction} />
        </div>
      )}
    </div>
  );
};

export default HomeTab;
